import { type FC, useMemo, useState } from "react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { sentryUtils } from "@/shared/utils/sentry";
import { currencyInput } from "@/shared/utils/currencyInput";
import { FieldError } from "@/shared/components/ui/FieldError";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import { ALL_CHECKLIST_KEYS, NONE_VALUE } from "./OtherAssetsChecklist";

interface AssetFieldConfig {
  institutionLabel: string;
  placeholder: string;
  category: string;
  itemType: string;
}

const ASSET_FIELD_CONFIG: Record<string, AssetFieldConfig> = {
  loans_made: {
    institutionLabel: "Counterparty or Employer",
    placeholder: "Person or company that owes the money",
    category: "Receivables",
    itemType: "Loans made to someone else",
  },
  final_paychecks: {
    institutionLabel: "Employer",
    placeholder: "Company that owes wages",
    category: "Receivables",
    itemType: "Final paychecks or unpaid wages",
  },
  refunds_in_process: {
    institutionLabel: "Source",
    placeholder: "Company or agency that owes the refund",
    category: "Receivables",
    itemType: "Refunds in process",
  },
  legal_settlements: {
    institutionLabel: "Counterparty",
    placeholder: "Person or company that owes the settlement",
    category: "Receivables",
    itemType: "Legal settlements",
  },
  ltc_benefits_owed: {
    institutionLabel: "Insurance company",
    placeholder: "LTC insurance provider",
    category: "Receivables",
    itemType: "Long-term care benefits owed",
  },
  vested_stock_options: {
    institutionLabel: "Employer",
    placeholder: "The company they worked for",
    category: "Work-related pay and shares",
    itemType: "Vested stock options (ISOs / NSOs)",
  },
  restricted_stock_units: {
    institutionLabel: "Employer",
    placeholder: "The company they worked for",
    category: "Work-related pay and shares",
    itemType: "Restricted stock units",
  },
  deferred_compensation: {
    institutionLabel: "Employer",
    placeholder: "The company they worked for",
    category: "Work-related pay and shares",
    itemType: "Deferred compensation plans",
  },
  executive_retirement: {
    institutionLabel: "Employer",
    placeholder: "The company they worked for",
    category: "Work-related pay and shares",
    itemType: "Executive retirement plans",
  },
  patents: {
    institutionLabel: "Publisher or Licensee",
    placeholder: "e.g., Penguin Random House, Sony Music",
    category: "Royalties and Intellectual Property",
    itemType: "Patents",
  },
  book_music_royalties: {
    institutionLabel: "Publisher or Licensee",
    placeholder: "e.g., Penguin Random House, Sony Music",
    category: "Royalties and Intellectual Property",
    itemType: "Books or music royalties",
  },
  trademark_licensing: {
    institutionLabel: "Publisher or Licensee",
    placeholder: "e.g., Penguin Random House, Sony Music",
    category: "Royalties and Intellectual Property",
    itemType: "Trademark or licensing deal",
  },
  prepaid_funeral: {
    institutionLabel: "Funeral home",
    placeholder: "e.g., Smith Funeral Home",
    category: "Prepaid Services and Credits",
    itemType: "Prepaid funeral plan",
  },
  gift_cards: {
    institutionLabel: "Retailer",
    placeholder: "e.g., Amazon, Target, Starbucks",
    category: "Prepaid Services and Credits",
    itemType: "Gift cards and store credits",
  },
  airline_miles: {
    institutionLabel: "Program",
    placeholder: "e.g., Delta SkyMiles, Marriott Bonvoy",
    category: "Prepaid Services and Credits",
    itemType: "Airline miles and loyalty points",
  },
};

interface OtherAssetFormValues {
  institution: string;
  accountId: string;
  value: string;
  documentFile: File | null;
}

const EMPTY_FORM: OtherAssetFormValues = {
  institution: "",
  accountId: "",
  value: "",
  documentFile: null,
};

interface AddOtherAssetProps extends FlowCustomComponentProps {
  assetKey: string;
}

export const AddOtherAsset: FC<AddOtherAssetProps> = ({ assetKey, goToScreen, answers }) => {
  const config = ASSET_FIELD_CONFIG[assetKey];
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const firstName = person?.first_name ?? "your loved one";
  const { addItem, isAddingItem } = useEstateItems(estateId);

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    OtherAssetFormValues,
    { institution?: string }
  >(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valueInput = currencyInput(form.value, (val) => updateForm({ ...form, value: val }));

  const rawSelected = answers["other_assets_selected"];
  const selected: string[] = useMemo(() => {
    const raw = Array.isArray(rawSelected) ? rawSelected.filter((v) => v !== NONE_VALUE) : [];
    return ALL_CHECKLIST_KEYS.filter((key) => raw.includes(key));
  }, [rawSelected]);

  const currentIndex = selected.indexOf(assetKey);
  const nextKey = currentIndex < selected.length - 1 ? selected[currentIndex + 1] : null;

  const advance = (updatedAnswers: Record<string, string | string[]>) => {
    if (nextKey) {
      goToScreen(`add_other_asset_${nextKey}`, updatedAnswers);
    } else {
      goToScreen("other_assets_review", {
        ...updatedAnswers,
        other_assets_add_done: "yes",
      });
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting || isAddingItem || !estateId || !config) return;

    if (!form.institution.trim()) {
      setFieldErrors({ institution: `Please enter the ${config.institutionLabel.toLowerCase()}` });
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      let sourceDocumentId: number | undefined;
      if (form.documentFile) {
        sourceDocumentId = await uploadDocumentWithAuth(form.documentFile, estateId, "other");
      }

      await addItem({
        estate_id: estateId,
        category: config.category,
        classification: "asset",
        item_type: config.itemType,
        item_name: form.institution.trim(),
        balance: parseFloat(form.value) || 0,
        metadata: {
          account_id: form.accountId.trim() || undefined,
        },
        source_document_id: sourceDocumentId,
      });

      advance({ ...answers, [`other_asset_${assetKey}_done`]: "yes" });
    } catch (err) {
      setError("Failed to add item. Please try again.");
      sentryUtils.captureError(err, "AddOtherAsset submission failed", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!config) return null;

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-center">
        Add {firstName}&apos;s {config.itemType.toLowerCase()}
      </h2>
      <p className="mt-2 text-center text-secondary">
        Share what you know. We'll help you gather details later.
      </p>
      <div className="mt-8 w-full">
        <AddItemLayout
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting || isAddingItem}
          error={error}
          onSkip={() => advance({ ...answers, [`other_asset_${assetKey}_done`]: "skipped" })}
          skipLabel="Skip for now"
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label required>{config.institutionLabel}</Label>
              <Input
                value={form.institution}
                onChange={(e) => updateForm({ ...form, institution: e.target.value })}
                placeholder={config.placeholder}
                className={fieldErrors.institution ? "border-error" : ""}
              />
              <FieldError message={fieldErrors.institution} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>
                Account identifier <span className="text-tertiary">(optional)</span>
              </Label>
              <Input
                value={form.accountId}
                onChange={(e) => updateForm({ ...form, accountId: e.target.value })}
                placeholder="e.g., account number or reference"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>
                Value <span className="text-tertiary">(optional)</span>
              </Label>
              <CurrencyInput
                value={valueInput.displayValue}
                onChange={valueInput.handleChange}
                placeholder="0.00"
              />
            </div>

            <UploadZone
              label="Related documents"
              labelSuffix="(optional)"
              file={form.documentFile}
              onFileSelect={(file) => updateForm({ ...form, documentFile: file })}
              onFileRemove={() => updateForm({ ...form, documentFile: null })}
            />
          </div>
        </AddItemLayout>
      </div>
    </div>
  );
};

export { ASSET_FIELD_CONFIG };
